// Flujo de "agregar vinilo" (Req. 8, 9, 7, 24).
// Mejoras v2 (Req. 24):
//   - Múltiples candidatas de Discogs (top 5 con % confianza), el usuario elige.
//   - Condición del disco y carátula obligatorias en la propuesta (Req. 24.7/24.8).
//   - Género y subgénero confirmables/editables en la propuesta (Req. 24.9).
//   - Ubicación sugerida con preview de renumeración integrado (Req. 24.16/24.17).
//   - Botón primario sobrio (Req. 24.1); subtítulo de card eliminado (Req. 24.2).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import type { Api } from '../api/endpoints.js';
import type { AcceptResponse, RenumberPreview } from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.js';
import { useOnboardingMaybe } from '../onboarding/OnboardingContext.js';
import { trackEvent } from '../analytics/analytics.js';
import { AlertMessage, ButtonSpinner, Card, Icon, PageHeader, Spinner } from '../ui/components.js';
import {
  ConditionSelect,
  CoverPicker,
  CustomFieldsSection,
  DateField,
  EditField,
  FormSection,
  GenreSubgenreFields,
  SelectField,
  YesNoField,
  missingCustomKeys,
  numStr,
  toNum,
  useRecordFieldConfig,
} from './vinylFields.js';
import type {
  ConditionGrade,
  IngestionJob,
  Record as VinylRecord,
  RecordProposalFields,
  ScoredCandidate,
} from '../types.js';

type Step = 'input' | 'processing' | 'candidates' | 'proposal' | 'renumber' | 'done';

/**
 * Normaliza una candidata de `discogs_candidates` a la forma `ScoredCandidate`
 * que consume la UI. El pipeline de ingesta puede persistir candidatas ya
 * puntuadas (con `discogsReleaseId` + `score`) o crudas de la búsqueda de
 * Discogs (con `releaseId` y sin `score`). Se aceptan ambas para que la lista de
 * selección se muestre siempre que Discogs devolvió resultados.
 */
function normalizeCandidate(raw: unknown): ScoredCandidate {
  const c = (raw ?? {}) as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
  return {
    // El id puede venir como `discogsReleaseId` (puntuada) o `releaseId` (cruda).
    discogsReleaseId: num(c.discogsReleaseId) ?? num(c.releaseId),
    discogsMasterId: num(c.discogsMasterId) ?? num(c.masterId),
    title: str(c.title) ?? '',
    year: num(c.year),
    label: str(c.label),
    catalogNumber: str(c.catalogNumber),
    country: str(c.country),
    format: str(c.format),
    coverImageUrl: str(c.coverImageUrl),
    // Sin score (candidata cruda) se trata como 0 para no romper el orden.
    score: num(c.score) ?? 0,
    breakdown: Array.isArray(c.breakdown) ? (c.breakdown as ScoredCandidate['breakdown']) : [],
  };
}

/**
 * Activación GA4 `first_record_cataloged` (RT-13). Emite el evento EXACTAMENTE
 * UNA vez por usuario de por vida, en el momento en que el backend confirma el
 * primer guardado del catálogo (contador 0→1). La unicidad es AUTORITATIVA en
 * el backend (no localStorage): se reclama una marca one-shot y el evento solo
 * se emite si ESTA llamada fue la que la reclamó. Así, borrar el disco y
 * catalogar otro NO re-emite, y es robusto entre dispositivos.
 *
 * Gating (todo debe cumplirse para emitir):
 *   1. El backend aún NO ha reclamado la marca (`firstRecordEventEmitted`).
 *   2. El contador de discos RESULTANTE es exactamente 1 (primer disco real).
 *   3. La reclamación atómica del backend devuelve `claimed === true`.
 *
 * `hours_since_signup` se calcula con `signupAt` (ISO de `app_user.created_at`);
 * si falta, se OMITE el evento (no se emite NaN). La ventana F0-07 (≤ 24 h) NO
 * se aplica aquí: se mide aguas abajo en GA4; siempre se emite el valor real.
 *
 * Best-effort: cualquier fallo (red, conteo) se traga en silencio para no
 * afectar el flujo de alta; la analítica nunca bloquea al usuario.
 */
async function maybeEmitFirstRecordEvent(args: {
  api: Api;
  signupAt: string | undefined;
  alreadyEmitted: boolean | undefined;
  catalogMethod: 'manual' | 'photo';
  onboardingActive: boolean;
  onClaimed: () => void;
}): Promise<void> {
  const { api, signupAt, alreadyEmitted, catalogMethod, onboardingActive, onClaimed } = args;
  try {
    // (1) Ya emitido antes (autoritativo en backend): nada que hacer.
    if (alreadyEmitted) return;
    // Sin `signupAt` no se puede calcular `hours_since_signup` sin arriesgar
    // NaN: se omite el evento de forma defensiva.
    if (!signupAt) return;
    // (2) Contador RESULTANTE: `total` de la lista acotada al tenant/usuario.
    const { total } = await api.records.list({ limit: 1 });
    if (total !== 1) return;
    // (3) Reclamación ATÓMICA one-shot en el backend. Solo emitimos si ESTA
    // llamada fue la que fijó la marca (evita doble emisión entre dispositivos).
    const { claimed } = await api.me.claimFirstRecordEvent();
    if (!claimed) return;

    const hoursSinceSignup =
      Math.round(((Date.now() - new Date(signupAt).getTime()) / 3600000) * 10) / 10;
    if (!Number.isFinite(hoursSinceSignup)) return;

    trackEvent('first_record_cataloged', {
      catalog_method: catalogMethod,
      hours_since_signup: hoursSinceSignup,
      records_count: 1,
      source_flow: onboardingActive ? 'onboarding' : 'collection',
    });
    // La marca ya está reclamada en backend: refrescamos el estado local para
    // que un segundo guardado en la misma sesión no reintente la reclamación.
    onClaimed();
  } catch {
    // Best-effort: la analítica no debe interferir con el alta del disco.
  }
}

export function AddVinylView() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const { permissions, reloadPermissions } = useAuth();
  const onboarding = useOnboardingMaybe();
  const [step, setStep] = useState<Step>('input');
  const [mode, setMode] = useState<'manual' | 'photo'>('manual');
  const [manual, setManual] = useState({ artist: '', title: '', catalogNumber: '' });
  const [files, setFiles] = useState<File[]>([]);
  // URLs de vista previa (una por archivo, en el mismo orden que `files`).
  const [previews, setPreviews] = useState<string[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<IngestionJob | null>(null);
  const [candidates, setCandidates] = useState<ScoredCandidate[]>([]);
  // Candidata elegida en Resultados: conserva sus ids de Discogs para enviarlos
  // al aceptar (portada + detección de duplicados usan el release ELEGIDO).
  const [selectedCandidate, setSelectedCandidate] = useState<ScoredCandidate | null>(null);
  const [proposal, setProposal] = useState<RecordProposalFields | null>(null);
  // Config de campos del tenant (Req. campos configurables): obligatoriedad
  // dinámica + campos personalizados a pedir en el ALTA.
  const fieldConfig = useRecordFieldConfig('add');
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});
  // Disco del catálogo que podría ser el mismo (posible duplicado, Req. 9.8).
  // Se detecta al buscar; se avisa ANTES de continuar con la propuesta.
  const [duplicateRecord, setDuplicateRecord] = useState<VinylRecord | null>(null);
  const [dupeDismissed, setDupeDismissed] = useState(false);
  const [renumber, setRenumber] = useState<RenumberPreview | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Activa el resaltado en rojo de los campos obligatorios faltantes tras un
  // intento de continuar. El borde vuelve a normal en cuanto el campo se llena.
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Libera las URLs de vista previa al desmontar (evita fugas de memoria).
  useEffect(
    () => () => {
      setPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
    },
    [],
  );

  /** Agrega fotos a la selección (sin reemplazar las ya elegidas). */
  function addFiles(picked: File[]) {
    if (picked.length === 0) return;
    setFiles((prev) => [...prev, ...picked]);
    setPreviews((prev) => [...prev, ...picked.map((f) => URL.createObjectURL(f))]);
  }

  /** Elimina la foto en el índice indicado y libera su vista previa. */
  function removeFileAt(idx: number) {
    setPreviews((prev) => {
      const url = prev[idx];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== idx);
    });
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  /** Quita todas las fotos seleccionadas. */
  function clearFiles() {
    setPreviews((prev) => {
      prev.forEach((u) => URL.revokeObjectURL(u));
      return [];
    });
    setFiles([]);
  }

  async function uploadPhotos(): Promise<string[]> {
    const keys: string[] = [];
    for (const file of files) {
      const { upload_url, s3_key } = await api.ingestion.createUpload(
        file.type || 'image/jpeg',
        file.name,
      );
      await fetch(upload_url, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'image/jpeg' },
        body: file,
      });
      keys.push(s3_key);
    }
    return keys;
  }

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const imageKeys = mode === 'photo' ? await uploadPhotos() : [];
      const res = await api.ingestion.startJob({
        image_s3_keys: imageKeys,
        manual:
          mode === 'manual'
            ? manual
            : { artist: manual.artist || undefined, title: manual.title || undefined },
      });
      setJobId(res.id);
      setStep('processing');
      poll(res.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('addVinyl.errStartIngest'));
    } finally {
      setBusy(false);
    }
  }

  function poll(id: string) {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const j = await api.ingestion.getJob(id);
        setJob(j);
        if (j.status === 'proposed' && j.proposal) {
          stopPolling();
          // Candidatas de Discogs (top 5, Req. 24.19). El pipeline puede
          // persistirlas ya puntuadas (`ScoredCandidate` con `discogsReleaseId` +
          // `score`) o crudas de la búsqueda (`DiscogsCandidate` con `releaseId` y
          // sin `score`). Se normaliza para tolerar ambas formas: así el usuario
          // puede elegir el release aunque no haya score todavía.
          const rawCandidates = Array.isArray(j.discogs_candidates) ? j.discogs_candidates : [];
          const topCandidates = rawCandidates
            .map(normalizeCandidate)
            .filter((c) => c.discogsReleaseId != null)
            .sort((a, b) => b.score - a.score)
            .slice(0, 12);
          setCandidates(topCandidates);
          // Poblar la carátula por defecto con la primera imagen del release si
          // el agente no fijó `cover_image_url` (para que el selector tenga una
          // selección inicial y esa imagen se guarde al aceptar).
          const prop = j.proposal.proposal;
          if (!prop.cover_image_url && prop.cover_image_urls && prop.cover_image_urls.length > 0) {
            prop.cover_image_url = prop.cover_image_urls[0]!;
          }
          // Tamaño por defecto 12" (el más común en vinilos) si el agente no lo
          // determinó, para no dejar vacío un campo obligatorio.
          if (prop.size_inches == null) {
            prop.size_inches = 12;
          }
          setProposal(prop);
          // Posible duplicado (Req. 9.8): si la búsqueda detectó un disco del
          // catálogo que podría ser el mismo, se carga para AVISAR de forma
          // clara antes de continuar (no un error tardío al aceptar).
          const dupe = j.proposal.duplicate_warning;
          if (dupe?.is_possible_duplicate && dupe.record_id) {
            setDupeDismissed(false);
            void api.records
              .get(dupe.record_id)
              .then((rec) => setDuplicateRecord(rec))
              .catch(() => setDuplicateRecord(null));
          } else {
            setDuplicateRecord(null);
          }
          // Mostrar la selección siempre que haya al menos una candidata, para
          // que el usuario pueda elegir cuál release usar. Solo se salta a la
          // propuesta directa cuando no hubo candidatas de Discogs.
          setStep(topCandidates.length > 0 ? 'candidates' : 'proposal');
        } else if (j.status === 'error') {
          stopPolling();
          setError(j.error ?? t('addVinyl.ingestFailed'));
          setStep('input');
        }
      } catch {
        // Reintenta en el próximo tick.
      }
    }, 2000);
  }

  function selectCandidate(c: ScoredCandidate) {
    // Aplicar TODOS los campos de la candidata elegida a la propuesta, para que
    // la pantalla de confirmación refleje exactamente el release seleccionado
    // (antes solo copiaba sello/catálogo/país/formato, así que el título/artista
    // seguían mostrando la propuesta original del agente). El `title` de Discogs
    // viene como "Artista - Título": se separa para poblar ambos campos.
    const { artist, title } = splitArtistTitle(c.title);
    setSelectedCandidate(c);
    // Limpiar el aviso de duplicado del agente (se calculó para el release
    // ORIGINAL, no para esta candidata). Si el nuevo release también es un
    // duplicado, la red de seguridad del backend al aceptar lo detectará y
    // mostrará el aviso correcto (showDuplicateFromAccept).
    setDuplicateRecord(null);
    setDupeDismissed(false);
    setProposal((p) =>
      p
        ? {
            ...p,
            artist: artist ?? p.artist,
            title: title ?? p.title,
            edition_year: c.year ?? p.edition_year,
            label: c.label ?? p.label,
            label_catalog_nr: c.catalogNumber ?? p.label_catalog_nr,
            origin_country: c.country ?? p.origin_country,
            discogs_format: c.format ?? p.discogs_format,
            // Portada del release elegido (para guardar la copia local al aceptar).
            cover_image_url: c.coverImageUrl ?? p.cover_image_url,
          }
        : p,
    );
    setStep('proposal');
  }

  function updateProposal(patch: Partial<RecordProposalFields>) {
    setProposal((p) => (p ? { ...p, ...patch } : p));
  }

  async function loadRenumberPreview(recordId: string) {
    try {
      const preview = await api.records.renumberPreview(recordId);
      setRenumber(preview);
    } catch {
      setRenumber(null);
    }
  }

  async function goToRenumber() {
    if (!proposal) return;
    // Validación completa de los campos obligatorios (Req. 2.1/2.5 + campos
    // configurables): se resaltan en rojo los que faltan y se listan en el
    // mensaje. La obligatoriedad estándar es la del tenant; además se validan
    // los campos personalizados obligatorios.
    const missing = missingRequiredKeys(proposal, fieldConfig.requiredFrontKeys);
    const missingCust = missingCustomKeys(fieldConfig.customFields, customValues);
    if (missing.size > 0 || missingCust.length > 0) {
      setShowRequiredErrors(true);
      const labels = [...missing].map((k) => t(`recordForm.requiredLabels.${k}`));
      const custLabels = fieldConfig.customFields
        .filter((f) => missingCust.includes(f.key))
        .map((f) => f.label);
      setError(t('recordForm.missingRequired', { fields: [...labels, ...custLabels].join(', ') }));
      return;
    }
    setShowRequiredErrors(false);
    setError(null);
    if (!createdId) {
      // Crear el disco y AVANZAR AL PASO "ALMACENAR" (no a "Listo"): allí se
      // muestra la numeración asignada y la cascada de renumeración (Req. 7).
      await confirm(true);
    } else {
      await loadRenumberPreview(createdId);
      setStep('renumber');
    }
  }

  async function confirm(goToRenumberAfter: boolean) {
    if (!jobId || !proposal) return;
    const missing = missingRequiredKeys(proposal, fieldConfig.requiredFrontKeys);
    const missingCust = missingCustomKeys(fieldConfig.customFields, customValues);
    if (missing.size > 0 || missingCust.length > 0) {
      setShowRequiredErrors(true);
      const labels = [...missing].map((k) => t(`recordForm.requiredLabels.${k}`));
      const custLabels = fieldConfig.customFields
        .filter((f) => missingCust.includes(f.key))
        .map((f) => f.label);
      setError(t('recordForm.missingRequired', { fields: [...labels, ...custLabels].join(', ') }));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        record: {
          ...proposalToRecordInput(proposal, selectedCandidate),
          // Campos personalizados del tenant (Req. campos configurables).
          custom_fields: customValues,
        },
        // Si el usuario ya vio el aviso de duplicado y decidió continuar, se
        // fuerza la creación para que el backend no lo frene de nuevo.
        force_new: dupeDismissed,
      };
      const res: AcceptResponse = await api.ingestion.accept(jobId, body);
      if (res.record_id) {
        setCreatedId(res.record_id);
        // Activación GA4 `first_record_cataloged` (RT-13): ESTE es el único seam
        // (guardado CONFIRMADO por el backend), no la cámara/carga/preview. Se
        // emite una sola vez por usuario de por vida (gating autoritativo en
        // backend). Best-effort: no bloquea ni afecta el flujo de alta.
        void maybeEmitFirstRecordEvent({
          api,
          signupAt: permissions?.signupAt,
          alreadyEmitted: permissions?.firstRecordEventEmitted,
          catalogMethod: mode,
          onboardingActive: onboarding?.active ?? false,
          onClaimed: () => {
            void reloadPermissions();
          },
        });
        if (goToRenumberAfter) {
          await loadRenumberPreview(res.record_id);
          setStep('renumber');
        } else {
          setStep('done');
        }
      } else if (res.error === 'duplicate_match' || res.actions) {
        // Duplicado detectado al aceptar (no se había avisado antes): mostrar el
        // aviso CLARO en vez de un error técnico. Se carga el disco existente.
        await showDuplicateFromAccept(res);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('addVinyl.errConfirm'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Ante un `duplicate_match` devuelto al aceptar, carga el disco existente y
   * muestra el aviso claro (banner) en el paso Propuesta, en lugar de un mensaje
   * de error técnico. El id del existente viene en `res.match.id`.
   */
  async function showDuplicateFromAccept(res: AcceptResponse) {
    const existingId = res.match?.id;
    if (existingId) {
      try {
        const rec = await api.records.get(existingId);
        setDuplicateRecord(rec);
        setDupeDismissed(false);
        setStep('proposal');
        return;
      } catch {
        // Si no se puede cargar, cae al mensaje genérico abajo.
      }
    }
    setError(t('addVinyl.errDuplicateGeneric'));
  }

  async function applyRenumber() {
    if (!createdId) return;
    setBusy(true);
    try {
      await api.records.renumberApply(createdId);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('addVinyl.errRenumber'));
    } finally {
      setBusy(false);
    }
  }

  const steps: { id: Step; label: string }[] = [
    { id: 'input', label: t('addVinyl.steps.input') },
    { id: 'processing', label: t('addVinyl.steps.processing') },
    { id: 'candidates', label: t('addVinyl.steps.candidates') },
    { id: 'proposal', label: t('addVinyl.steps.proposal') },
    { id: 'renumber', label: t('addVinyl.steps.renumber') },
    { id: 'done', label: t('addVinyl.steps.done') },
  ];
  const currentStepIdx = steps.findIndex((s) => s.id === step);

  // Campos obligatorios faltantes (se reevalúa en cada render): se resaltan en
  // rojo solo tras un intento de continuar (`showRequiredErrors`). Al llenar un
  // campo, sale del conjunto y su borde vuelve al normal.
  const missingKeys = proposal
    ? missingRequiredKeys(proposal, fieldConfig.requiredFrontKeys)
    : new Set<RequiredKey>();
  const invalid = (key: RequiredKey) => showRequiredErrors && missingKeys.has(key);
  // Obligatoriedad dinámica (asterisco y validación) por cuenta.
  const req = (key: RequiredKey) => fieldConfig.requiredFrontKeys.has(key);
  // Campos personalizados obligatorios que faltan (resalte + bloqueo al guardar).
  const missingCustom = new Set(missingCustomKeys(fieldConfig.customFields, customValues));

  /**
   * Navegación del stepper: solo se puede RETROCEDER a un paso anterior ya
   * visitado (no avanzar saltando pasos). Además, algunos destinos no son
   * navegables manualmente: `processing` (transitorio) y `done` (final). Y una
   * vez creado el disco (`createdId`), no se permite volver a `proposal` para no
   * reconfirmar y duplicar el ingreso: en ese punto solo se avanza a Almacenar.
   */
  function canGoToStep(targetIdx: number): boolean {
    if (targetIdx >= currentStepIdx) return false; // solo hacia atrás
    const target = steps[targetIdx]!.id;
    if (target === 'processing' || target === 'done') return false;
    // Tras crear el disco, `proposal`/`candidates` ya no son un destino seguro.
    if (createdId && (target === 'proposal' || target === 'candidates' || target === 'input')) {
      return false;
    }
    return true;
  }

  function goToStep(targetIdx: number) {
    if (!canGoToStep(targetIdx)) return;
    setError(null);
    setStep(steps[targetIdx]!.id);
  }

  return (
    <div className="flex flex-col">
      <PageHeader title={t('addVinyl.pageTitle')} subtitle={t('addVinyl.pageSubtitle')} />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        {/* Progreso del wizard. Los pasos previos ya visitados actúan como
            botones para RETROCEDER (no se puede avanzar saltando pasos). */}
        <div className="flex items-center gap-1">
          {steps.map((s, i) => {
            const clickable = canGoToStep(i);
            return (
              <button
                key={s.id}
                type="button"
                disabled={!clickable}
                onClick={() => goToStep(i)}
                aria-current={i === currentStepIdx ? 'step' : undefined}
                title={clickable ? t('addVinyl.backToStep', { step: s.label }) : undefined}
                className={`flex-1 flex flex-col items-center gap-1 ${clickable ? 'cursor-pointer group' : 'cursor-default'}`}
              >
                <div
                  className={`w-full h-1 rounded-full transition-colors ${
                    i <= currentStepIdx ? 'bg-primary-container' : 'bg-surface-container-low'
                  } ${clickable ? 'group-hover:bg-primary' : ''}`}
                />
                <span
                  className={`font-badge-micro text-badge-micro uppercase transition-colors ${
                    i === currentStepIdx
                      ? 'text-primary'
                      : clickable
                        ? 'text-primary-container group-hover:text-primary'
                        : 'text-on-surface-variant'
                  }`}
                >
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>

        <AlertMessage message={error} />

        {/* ── PASO: CAPTURA ── */}
        {step === 'input' && (
          <Card>
            <div
              data-tour="add-capture-modes"
              className="flex items-center gap-space-xs p-1 rounded-lg bg-surface-deck border border-border-subtle mb-space-md"
            >
              {(['manual', 'photo'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`flex-1 h-10 rounded flex items-center justify-center gap-space-xs font-body-sm text-body-sm transition-colors ${mode === m ? 'bg-surface-rack text-primary' : 'text-on-surface-variant'}`}
                >
                  <Icon
                    name={m === 'manual' ? 'keyboard' : 'photo_camera'}
                    className="text-[16px]"
                  />
                  {m === 'manual' ? t('addVinyl.modeManual') : t('addVinyl.modePhoto')}
                </button>
              ))}
            </div>

            {mode === 'manual' ? (
              <div className="grid grid-cols-1 gap-space-sm">
                <EditField
                  label={t('addVinyl.manualArtist')}
                  value={manual.artist}
                  onChange={(v) => setManual({ ...manual, artist: v })}
                />
                <EditField
                  label={t('addVinyl.manualTitle')}
                  value={manual.title}
                  onChange={(v) => setManual({ ...manual, title: v })}
                />
                <EditField
                  label={t('addVinyl.manualCatalog')}
                  value={manual.catalogNumber}
                  onChange={(v) => setManual({ ...manual, catalogNumber: v })}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-space-sm">
                {files.length === 0 ? (
                  <label className="field-slot h-28 flex-col justify-center cursor-pointer border-dashed">
                    <Icon name="add_a_photo" className="text-vacuum-glow text-[32px]" />
                    <span className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                      {t('addVinyl.capturePrompt')}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        addFiles(Array.from(e.target.files ?? []));
                        e.target.value = '';
                      }}
                    />
                  </label>
                ) : (
                  <>
                    {/* Miniaturas: cada foto puede eliminarse individualmente. */}
                    <div className="grid grid-cols-3 gap-space-sm">
                      {previews.map((url, idx) => (
                        <div
                          key={url}
                          className="relative aspect-square rounded-lg border border-border-subtle bg-surface-deck overflow-hidden"
                        >
                          <img
                            src={url}
                            alt={t('addVinyl.photoAlt', { n: idx + 1 })}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            className="absolute top-1 right-1 w-7 h-7 inline-flex items-center justify-center rounded-full bg-surface-base/80 backdrop-blur border border-error/40 text-error active:scale-95 transition-transform disabled:opacity-50"
                            disabled={busy}
                            onClick={() => removeFileAt(idx)}
                            aria-label={t('addVinyl.removePhoto', { n: idx + 1 })}
                          >
                            <Icon name="close" className="text-[16px]" />
                          </button>
                        </div>
                      ))}
                      {/* Celda para añadir más fotos. */}
                      <label className="aspect-square rounded-lg border border-dashed border-border-subtle bg-surface-container-lowest/40 flex flex-col items-center justify-center gap-1 cursor-pointer text-on-surface-variant active:scale-95 transition-transform">
                        <Icon name="add_a_photo" className="text-[24px]" />
                        <span className="font-badge-micro text-badge-micro">
                          {t('addVinyl.addPhoto')}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            addFiles(Array.from(e.target.files ?? []));
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                    <div className="flex items-center justify-between gap-space-sm">
                      <p className="font-code-catalog text-body-sm text-primary">
                        {t('addVinyl.photosSelected', { count: files.length })}
                      </p>
                      <button
                        type="button"
                        className="btn-ghost btn-sm text-error"
                        disabled={busy}
                        onClick={clearFiles}
                      >
                        <Icon name="delete_sweep" className="text-[16px]" />{' '}
                        {t('addVinyl.removeAll')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <button
              className="btn-primary w-full mt-space-md"
              type="button"
              data-tour="add-search-discogs"
              disabled={busy || (mode === 'photo' && files.length === 0)}
              onClick={() => void start()}
            >
              {busy ? <ButtonSpinner /> : <Icon name="travel_explore" className="text-[18px]" />}
              {busy ? t('addVinyl.starting') : t('addVinyl.searchDiscogs')}
            </button>
          </Card>
        )}

        {/* ── PASO: BÚSQUEDA ── */}
        {step === 'processing' && (
          <Card title={t('addVinyl.searching')} icon="autorenew">
            <Spinner
              label={t('addVinyl.processingStatus', {
                status: job?.status ?? t('addVinyl.statusPending'),
              })}
            />
            <p className="font-body-sm text-body-sm text-on-surface-variant text-center">
              {t('addVinyl.searchingDiscogs')}
            </p>
          </Card>
        )}

        {/* ── PASO: RESULTADOS (candidatas) ── */}
        {step === 'candidates' && (
          <div className="flex flex-col gap-space-sm">
            {/* Aviso de posible duplicado ANTES de que el usuario continúe */}
            {duplicateRecord && !dupeDismissed && (
              <DuplicateWarningBanner
                record={duplicateRecord}
                reason={job?.proposal?.duplicate_warning?.reason}
                onViewExisting={() => navigate(`/records/${duplicateRecord.id}`)}
                onContinue={() => setDupeDismissed(true)}
              />
            )}
            <p className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">
              {t('addVinyl.resultsCount', { count: candidates.length })}
            </p>
            {candidates.map((c, idx) => (
              <CandidateCard
                key={c.discogsReleaseId ?? `cand-${idx}`}
                candidate={c}
                onSelect={() => selectCandidate(c)}
              />
            ))}
            <button className="btn-ghost w-full" type="button" onClick={() => setStep('proposal')}>
              {t('addVinyl.useAgentProposal')}
            </button>
          </div>
        )}

        {/* ── PASO: PROPUESTA ── */}
        {step === 'proposal' && proposal && (
          <Card title={t('addVinyl.proposalTitle')} icon="auto_awesome">
            {duplicateRecord && !dupeDismissed && (
              <div className="mb-space-sm">
                <DuplicateWarningBanner
                  record={duplicateRecord}
                  reason={job?.proposal?.duplicate_warning?.reason}
                  onViewExisting={() => navigate(`/records/${duplicateRecord.id}`)}
                  onContinue={() => setDupeDismissed(true)}
                />
              </div>
            )}

            {/* ── Identificación (campos mínimos, marcados con *) ── */}
            <FormSection title={t('recordForm.sections.identification')}>
              <div className="grid grid-cols-2 gap-space-sm">
                <EditField
                  label={t('recordForm.artist')}
                  required={req('artist')}
                  invalid={invalid('artist')}
                  value={proposal.artist}
                  onChange={(v) => updateProposal({ artist: v })}
                />
                <EditField
                  label={t('recordForm.title')}
                  required={req('title')}
                  invalid={invalid('title')}
                  value={proposal.title}
                  onChange={(v) => updateProposal({ title: v })}
                />
                {/* Género propio + subgénero anidados (Req. 24.9); obligatoriedad por tenant. */}
                <GenreSubgenreFields
                  genre={proposal.genre_propio}
                  subgenre={proposal.subgenre ?? null}
                  onGenreChange={(v) => updateProposal({ genre_propio: v })}
                  onSubgenreChange={(v) => updateProposal({ subgenre: v })}
                  genreInvalid={invalid('genre_propio')}
                  subgenreInvalid={invalid('subgenre')}
                  genreRequired={req('genre_propio')}
                  subgenreRequired={req('subgenre')}
                />
                <EditField
                  label={t('recordForm.label')}
                  required={req('label')}
                  invalid={invalid('label')}
                  value={proposal.label}
                  onChange={(v) => updateProposal({ label: v })}
                />
                <EditField
                  label={t('recordForm.catalogNr')}
                  required={req('label_catalog_nr')}
                  invalid={invalid('label_catalog_nr')}
                  value={proposal.label_catalog_nr}
                  onChange={(v) => updateProposal({ label_catalog_nr: v })}
                />
                <EditField
                  label={t('recordForm.editionYear')}
                  required={req('edition_year')}
                  invalid={invalid('edition_year')}
                  value={numStr(proposal.edition_year)}
                  onChange={(v) => updateProposal({ edition_year: toNum(v) })}
                />
                <EditField
                  label={t('recordForm.originalYear')}
                  value={numStr(proposal.original_release_year)}
                  onChange={(v) => updateProposal({ original_release_year: toNum(v) })}
                />
                <EditField
                  label={t('recordForm.origin')}
                  required={req('origin_country')}
                  invalid={invalid('origin_country')}
                  value={proposal.origin_country}
                  onChange={(v) => updateProposal({ origin_country: v })}
                />
              </div>
            </FormSection>

            {/* ── Formato ── */}
            <FormSection title={t('recordForm.sections.format')}>
              <div className="grid grid-cols-2 gap-space-sm">
                <SelectField
                  label={t('recordForm.type')}
                  required={req('type')}
                  invalid={invalid('type')}
                  value={proposal.type}
                  options={['Album', 'Single', 'Soundtrack', 'EP', 'Other']}
                  onChange={(v) => updateProposal({ type: v })}
                />
                <SelectField
                  label={t('recordForm.sizeInches')}
                  required={req('size_inches')}
                  invalid={invalid('size_inches')}
                  value={proposal.size_inches != null ? String(proposal.size_inches) : null}
                  options={['12', '10', '7']}
                  onChange={(v) => updateProposal({ size_inches: toNum(v ?? '') })}
                />
                <EditField
                  label={t('recordForm.discsPerAlbum')}
                  required={req('discs_per_album')}
                  invalid={invalid('discs_per_album')}
                  value={numStr(proposal.discs_per_album)}
                  onChange={(v) => updateProposal({ discs_per_album: toNum(v) })}
                />
                <EditField
                  label={t('recordForm.copies')}
                  required={req('copies')}
                  invalid={invalid('copies')}
                  value={numStr(proposal.copies ?? 1)}
                  onChange={(v) => updateProposal({ copies: toNum(v) })}
                />
                <EditField
                  label={t('recordForm.vinylColor')}
                  value={proposal.vinyl_color ?? null}
                  onChange={(v) => updateProposal({ vinyl_color: v || null })}
                />
                <EditField
                  label={t('recordForm.grams')}
                  value={numStr(proposal.grams ?? null)}
                  onChange={(v) => updateProposal({ grams: toNum(v) })}
                />
                <EditField
                  label={t('recordForm.discogsFormat')}
                  value={proposal.discogs_format}
                  onChange={(v) => updateProposal({ discogs_format: v })}
                />
                <EditField
                  label={t('addVinyl.fmtBarcode')}
                  value={proposal.barcode}
                  onChange={(v) => updateProposal({ barcode: v || null })}
                />
              </div>
            </FormSection>

            {/* ── Estado físico (incluye la Condición obligatoria al inicio) ── */}
            <FormSection title={t('recordForm.sections.physicalCondition')}>
              <div className="grid grid-cols-2 gap-space-sm rounded-lg p-space-sm mb-space-sm border border-border-subtle">
                <ConditionSelect
                  label={t('addVinyl.mediaConditionLong')}
                  required={req('media_condition')}
                  invalid={invalid('media_condition')}
                  value={proposal.media_condition ?? null}
                  onChange={(v) => updateProposal({ media_condition: v as ConditionGrade })}
                />
                <ConditionSelect
                  label={t('addVinyl.sleeveConditionLong')}
                  required={req('sleeve_condition')}
                  invalid={invalid('sleeve_condition')}
                  value={proposal.sleeve_condition ?? null}
                  onChange={(v) => updateProposal({ sleeve_condition: v as ConditionGrade })}
                />
              </div>
              <div className="grid grid-cols-2 gap-space-sm">
                <YesNoField
                  label={t('addVinyl.hasSleeveQ')}
                  value={proposal.has_sleeve ?? null}
                  onChange={(v) => updateProposal({ has_sleeve: v })}
                />
                <YesNoField
                  label={t('addVinyl.hasCoverQ')}
                  value={proposal.has_cover ?? null}
                  onChange={(v) => updateProposal({ has_cover: v })}
                />
                <YesNoField
                  label={t('addVinyl.originalCoverQ')}
                  value={proposal.original_cover ?? null}
                  onChange={(v) => updateProposal({ original_cover: v })}
                />
                <DateField
                  label={t('recordForm.lastCleaned')}
                  value={proposal.last_cleaned ?? null}
                  onChange={(v) => updateProposal({ last_cleaned: v || null })}
                />
              </div>
            </FormSection>

            {/* ── Carátula: elegir cuál imagen guardar (Req.) ── */}
            {(proposal.cover_image_urls?.length ?? 0) > 0 && (
              <FormSection title={t('recordForm.sections.cover')}>
                <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-xs">
                  {(proposal.cover_image_urls?.length ?? 0) > 1
                    ? t('addVinyl.coverPickMultiple')
                    : t('addVinyl.coverPickSingle')}
                </p>
                <CoverPicker
                  images={proposal.cover_image_urls ?? []}
                  selected={proposal.cover_image_url}
                  onSelect={(url) => updateProposal({ cover_image_url: url })}
                />
              </FormSection>
            )}

            {/* ── Valor ── */}
            <FormSection title={t('addVinyl.sectionValue')}>
              <div className="grid grid-cols-2 gap-space-sm">
                <EditField
                  label={t('recordForm.commercialValue')}
                  value={numStr(proposal.commercial_value ?? null)}
                  onChange={(v) => updateProposal({ commercial_value: toNum(v) })}
                />
              </div>
            </FormSection>

            {/* ── Campos personalizados del tenant (Req. campos configurables) ── */}
            <CustomFieldsSection
              defs={fieldConfig.customFields}
              values={customValues}
              onChange={(key, value) => setCustomValues((prev) => ({ ...prev, [key]: value }))}
              invalidKeys={showRequiredErrors ? missingCustom : undefined}
            />

            {proposal.tracklist.length > 0 && (
              <p className="font-code-catalog text-body-sm text-primary mt-space-sm">
                {t('addVinyl.tracksDetected', { count: proposal.tracklist.length })}
              </p>
            )}

            <div className="flex items-center gap-space-sm mt-space-md">
              <button
                className="btn-primary flex-1"
                type="button"
                disabled={busy}
                onClick={() => void goToRenumber()}
              >
                {busy ? <ButtonSpinner /> : <Icon name="arrow_forward" className="text-[18px]" />}
                {busy ? t('addVinyl.processingBtn') : t('addVinyl.continue')}
              </button>
              <button
                className="btn-ghost"
                type="button"
                disabled={busy}
                onClick={() => {
                  if (jobId) void api.ingestion.discard(jobId);
                  setStep('input');
                }}
              >
                {t('addVinyl.discard')}
              </button>
            </div>
          </Card>
        )}

        {/* ── PASO: ALMACENAR (renumeración física, Req. 7) ── */}
        {step === 'renumber' && (
          <Card title={t('addVinyl.storeInRack')} icon="shelves">
            {/* Resumen: número interno que recibe el disco nuevo + su segmento. */}
            {renumber?.segment ? (
              <div className="p-space-sm rounded-lg bg-surface-rack border border-primary-container/40 mb-space-sm flex items-center justify-between gap-space-sm">
                <div className="flex flex-col">
                  <span className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">
                    {t('addVinyl.assignedPosition')}
                  </span>
                  <span className="font-body-sm text-body-sm text-on-surface">
                    {renumber.segment.genre_label ?? '—'}
                    {renumber.segment.physical_place
                      ? ` · ${t('addVinyl.box', { place: renumber.segment.physical_place })}`
                      : ''}
                  </span>
                </div>
                {renumber.assigned_number != null && (
                  <span className="font-code-catalog text-headline-sm text-primary font-bold">
                    #{renumber.assigned_number}
                  </span>
                )}
              </div>
            ) : (
              <div className="p-space-sm rounded-lg bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm mb-space-sm">
                {t('addVinyl.noSegment')}
              </div>
            )}

            {!renumber || renumber.changes.length === 0 ? (
              renumber?.segment ? (
                <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
                  {t('addVinyl.storedAtEnd')}
                </div>
              ) : null
            ) : (
              <>
                {renumber.segment_full && renumber.reorg && (
                  <div className="p-space-sm rounded-lg bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm mb-space-sm">
                    {t('addVinyl.segmentFull', { description: renumber.reorg.description })}
                  </div>
                )}
                <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-xs">
                  {t('addVinyl.shiftNotice', { count: renumber.changes.length })}
                </p>
                <ul className="flex flex-col gap-space-xs max-h-72 overflow-auto">
                  {renumber.changes.map((c) => (
                    <li
                      key={c.record_id}
                      className="surface-rack-card !p-space-sm flex items-center justify-between gap-space-sm"
                    >
                      <span className="font-body-sm text-body-sm text-on-surface truncate max-w-[45%]">
                        {c.artist ? `${c.artist} — ` : ''}
                        {c.title ?? c.record_id.slice(0, 8)}
                      </span>
                      <span className="flex items-center gap-space-xs font-code-catalog text-body-sm shrink-0">
                        <span className="text-on-surface-variant line-through">
                          {c.old_num ?? '—'}
                        </span>
                        <Icon
                          name="arrow_forward"
                          className="text-[14px] text-on-surface-variant"
                        />
                        <span className="text-goldmine-mint font-bold">{c.new_num ?? '—'}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="flex items-center gap-space-sm mt-space-md">
              {renumber && renumber.changes.length > 0 ? (
                <>
                  <button
                    className="btn-primary flex-1"
                    type="button"
                    disabled={busy}
                    onClick={() => void applyRenumber()}
                  >
                    {busy ? <ButtonSpinner /> : <Icon name="check" className="text-[18px]" />}
                    {busy ? t('addVinyl.applying') : t('addVinyl.acceptApply')}
                  </button>
                  <button
                    className="btn-ghost"
                    type="button"
                    disabled={busy}
                    onClick={() => setStep('done')}
                  >
                    {t('addVinyl.skip')}
                  </button>
                </>
              ) : (
                <button
                  className="btn-primary flex-1"
                  type="button"
                  disabled={busy}
                  onClick={() => setStep('done')}
                >
                  <Icon name="check" className="text-[18px]" /> {t('addVinyl.finishIngest')}
                </button>
              )}
            </div>
          </Card>
        )}

        {/* ── PASO: LISTO ── */}
        {step === 'done' && (
          <Card title={t('addVinyl.doneTitle')} icon="check_circle">
            <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm mb-space-md">
              {t('addVinyl.recordAdded')}
            </div>
            <div className="flex items-center gap-space-sm">
              {createdId && (
                <button
                  className="btn-primary flex-1"
                  type="button"
                  onClick={() => navigate(`/records/${createdId}`)}
                >
                  <Icon name="visibility" className="text-[18px]" /> {t('addVinyl.viewRecord')}
                </button>
              )}
              <button className="btn-secondary" type="button" onClick={() => reset()}>
                <Icon name="add" className="text-[18px]" /> {t('addVinyl.another')}
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );

  function reset() {
    setStep('input');
    setManual({ artist: '', title: '', catalogNumber: '' });
    setFiles([]);
    setJobId(null);
    setJob(null);
    setCandidates([]);
    setSelectedCandidate(null);
    setProposal(null);
    setDuplicateRecord(null);
    setDupeDismissed(false);
    setRenumber(null);
    setCreatedId(null);
    setError(null);
    setShowRequiredErrors(false);
  }
}

/**
 * Tarjeta de una candidata de Discogs en la pantalla de Resultados (Req. 24.19).
 * Muestra la CARÁTULA, artista/título destacados, el % de coincidencia, y los
 * campos más importantes con SUBTÍTULOS en una cuadrícula de columnas para
 * lectura fácil. Toda la tarjeta es un botón que selecciona la candidata.
 */
function CandidateCard({
  candidate: c,
  onSelect,
}: {
  candidate: ScoredCandidate;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { artist, title } = splitArtistTitle(c.title);
  const fields: { label: string; value: string | null }[] = [
    { label: t('addVinyl.candidate.year'), value: c.year != null ? String(c.year) : null },
    { label: t('addVinyl.candidate.label'), value: c.label ?? null },
    { label: t('addVinyl.candidate.catalogNr'), value: c.catalogNumber ?? null },
    { label: t('addVinyl.candidate.country'), value: c.country ?? null },
    { label: t('addVinyl.candidate.format'), value: c.format ?? null },
  ];
  return (
    <button
      type="button"
      onClick={onSelect}
      className="surface-card text-left w-full hover:border-primary-container/60 transition-colors"
    >
      <div className="flex items-start gap-space-sm">
        {/* Carátula del release. */}
        <div className="w-16 h-16 shrink-0 rounded-lg bg-surface-container-high border border-border-subtle overflow-hidden flex items-center justify-center">
          {c.coverImageUrl ? (
            <img
              src={c.coverImageUrl}
              alt={t('addVinyl.candidate.coverAlt', { title: title ?? c.title })}
              className="w-full h-full object-cover"
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
            />
          ) : (
            <Icon name="album" className="text-on-surface-variant text-[28px] opacity-50" />
          )}
        </div>

        {/* Artista + título + % de coincidencia. */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-space-sm">
            <div className="min-w-0">
              <p className="font-headline-sm text-headline-sm text-vacuum-glow truncate">
                {title ?? c.title}
              </p>
              {artist && (
                <p className="font-body-sm text-body-sm text-on-surface truncate">{artist}</p>
              )}
            </div>
            {c.score > 0 && (
              <span
                className={`shrink-0 font-code-catalog text-body-sm font-bold ${
                  c.score >= 80
                    ? 'text-goldmine-mint'
                    : c.score >= 60
                      ? 'text-goldmine-vg'
                      : 'text-on-surface-variant'
                }`}
              >
                {Math.round(c.score)}%
              </span>
            )}
          </div>
        </div>
        <Icon
          name="chevron_right"
          className="text-[18px] text-on-surface-variant shrink-0 self-center"
        />
      </div>

      {/* Campos importantes con subtítulos, en columnas. */}
      <dl className="grid grid-cols-2 gap-x-space-sm gap-y-space-xs mt-space-sm pt-space-sm border-t border-border-subtle">
        {fields
          .filter((f) => f.value)
          .map((f) => (
            <div key={f.label} className="flex flex-col min-w-0">
              <dt className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">
                {f.label}
              </dt>
              <dd className="font-body-sm text-body-sm text-on-surface truncate">{f.value}</dd>
            </div>
          ))}
      </dl>
    </button>
  );
}

/**
 * Aviso CLARO de posible duplicado (Req. 9.8): se muestra ANTES de continuar,
 * con los datos del disco existente y acciones para decidir. Reemplaza el aviso
 * técnico "match_found"/amarillo por una tarjeta accionable.
 */
function DuplicateWarningBanner({
  record,
  reason,
  onViewExisting,
  onContinue,
}: {
  record: VinylRecord;
  reason?: string;
  onViewExisting: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const meta = [
    record.artist_name ?? undefined,
    record.edition_year ? String(record.edition_year) : undefined,
    record.genre_name ?? undefined,
    record.internal_cat_num != null ? `#${record.internal_cat_num}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="rounded-xl border border-goldmine-vg/50 bg-goldmine-vg/10 p-space-md flex flex-col gap-space-sm">
      <div className="flex items-start gap-space-sm">
        <Icon name="warning" className="text-goldmine-vg text-[22px] shrink-0" />
        <div className="flex flex-col">
          <span className="font-headline-sm text-headline-sm text-goldmine-vg">
            {t('addVinyl.duplicate.title')}
          </span>
          <span className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">
            {reason ?? t('addVinyl.duplicate.defaultReason')}
          </span>
        </div>
      </div>

      {/* Ficha resumida del disco existente. */}
      <div className="flex items-center justify-between gap-space-sm rounded-lg bg-surface-rack border border-border-subtle p-space-sm">
        <div className="flex-1 min-w-0">
          <p className="font-headline-sm text-headline-sm text-on-surface truncate">
            {record.title}
          </p>
          {meta && (
            <p className="font-body-sm text-body-sm text-on-surface-variant truncate mt-0.5">
              {meta}
            </p>
          )}
          {record.copies > 1 && (
            <p className="font-badge-micro text-badge-micro text-on-surface-variant mt-0.5">
              {t('addVinyl.duplicate.alreadyCopies', { count: record.copies })}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-space-sm">
        <button type="button" className="btn-secondary flex-1" onClick={onViewExisting}>
          <Icon name="visibility" className="text-[18px]" /> {t('addVinyl.duplicate.viewExisting')}
        </button>
        <button type="button" className="btn-ghost flex-1" onClick={onContinue}>
          <Icon name="arrow_forward" className="text-[18px]" />{' '}
          {t('addVinyl.duplicate.continueAnyway')}
        </button>
      </div>
    </div>
  );
}

/** Clave de campo obligatorio (para marcar bordes individualmente). */
type RequiredKey =
  | 'artist'
  | 'title'
  | 'genre_propio'
  | 'subgenre'
  | 'label'
  | 'label_catalog_nr'
  | 'edition_year'
  | 'origin_country'
  | 'type'
  | 'size_inches'
  | 'discs_per_album'
  | 'copies'
  | 'media_condition'
  | 'sleeve_condition';

/**
 * Devuelve el CONJUNTO de claves de campos mínimos obligatorios que faltan en la
 * propuesta (Req. 2.1). Se reevalúa en cada render, así el borde rojo desaparece
 * en cuanto el usuario llena el campo.
 */
function missingRequiredKeys(p: RecordProposalFields, requiredKeys: Set<string>): Set<RequiredKey> {
  const hasText = (v: string | null | undefined) => typeof v === 'string' && v.trim() !== '';
  const hasNum = (v: number | null | undefined) =>
    typeof v === 'number' && Number.isFinite(v) && v > 0;
  const req = (k: RequiredKey) => requiredKeys.has(k);
  const missing = new Set<RequiredKey>();

  if (req('artist') && !hasText(p.artist)) missing.add('artist');
  if (req('title') && !hasText(p.title)) missing.add('title');
  if (req('genre_propio') && !hasText(p.genre_propio)) missing.add('genre_propio');
  if (req('subgenre') && !hasText(p.subgenre)) missing.add('subgenre');
  if (req('label') && !hasText(p.label)) missing.add('label');
  if (req('label_catalog_nr') && !hasText(p.label_catalog_nr)) missing.add('label_catalog_nr');
  if (req('edition_year') && !hasNum(p.edition_year)) missing.add('edition_year');
  if (req('origin_country') && !hasText(p.origin_country)) missing.add('origin_country');
  if (req('type') && !hasText(p.type)) missing.add('type');
  if (req('size_inches') && !hasNum(p.size_inches)) missing.add('size_inches');
  if (req('discs_per_album') && !hasNum(p.discs_per_album)) missing.add('discs_per_album');
  if (req('copies') && !hasNum(p.copies ?? 1)) missing.add('copies');
  if (req('media_condition') && !p.media_condition) missing.add('media_condition');
  if (req('sleeve_condition') && !p.sleeve_condition) missing.add('sleeve_condition');
  return missing;
}

/**
 * Convierte la propuesta editada a un RecordInput para aceptar (Req. 9.5).
 * Si el usuario eligió una candidata de Discogs, incluye su `discogs_release_id`
 * y `discogs_master_id` para que el backend descargue la portada correcta y
 * detecte duplicados sobre el release elegido (no el de la propuesta original).
 */
function proposalToRecordInput(
  p: RecordProposalFields,
  candidate: ScoredCandidate | null,
): Record<string, unknown> {
  return {
    title: p.title ?? '',
    artist_name: p.artist ?? null,
    // El sello va como nombre: el backend lo resuelve a `label_id` (findOrCreate).
    label_name: p.label ?? null,
    edition_year: p.edition_year,
    original_release_year: p.original_release_year,
    type: p.type ?? null,
    size_inches: p.size_inches ?? null,
    label_catalog_nr: p.label_catalog_nr,
    origin_country: p.origin_country,
    discogs_format: p.discogs_format,
    barcode: p.barcode,
    discs_per_album: p.discs_per_album ?? 1,
    copies: p.copies ?? 1,
    discogs_notes: p.discogs_notes,
    discogs_genre: p.discogs_genre,
    discogs_styles: p.discogs_styles,
    media_condition: p.media_condition ?? null,
    sleeve_condition: p.sleeve_condition ?? null,
    genre_propio: p.genre_propio ?? null,
    subgenre: p.subgenre ?? null,
    // Campos extendidos opcionales (Req. 2.2).
    commercial_value: p.commercial_value ?? null,
    vinyl_color: p.vinyl_color ?? null,
    grams: p.grams ?? null,
    has_sleeve: p.has_sleeve ?? null,
    has_cover: p.has_cover ?? null,
    original_cover: p.original_cover ?? null,
    last_cleaned: p.last_cleaned ?? null,
    comments: p.comments ?? null,
    // Ids de Discogs de la candidata ELEGIDA.
    discogs_release_id: candidate?.discogsReleaseId ?? null,
    discogs_master_id: candidate?.discogsMasterId ?? null,
    cover_image_url: p.cover_image_url ?? null,
  };
}

/**
 * Separa el formato "Artista - Título" de Discogs en sus partes. Si no hay
 * guión separador, se asume que todo es el título.
 */
function splitArtistTitle(raw: string): { artist: string | null; title: string | null } {
  if (!raw) return { artist: null, title: null };
  const idx = raw.indexOf(' - ');
  if (idx === -1) return { artist: null, title: raw.trim() || null };
  return {
    artist: raw.slice(0, idx).trim() || null,
    title: raw.slice(idx + 3).trim() || null,
  };
}
